import { useState } from "react";
import { Link } from "react-router-dom";
import { mediaUrl } from "../../api/client";
import { DownloadVideo } from "../../components/DownloadVideo";
import type { TemplateOption } from "../types";
import { disabledReason } from "./api";
import { editable, presetId, type Params } from "./params";
import { TemplateEditor } from "./TemplateEditor";

export function TemplateCard({ o, index, focused, selected, agent, clipId, params, onParams, onToggle, onFocus }: {
  o: TemplateOption; index: number; focused: boolean; selected: boolean; agent: boolean; clipId: number;
  params: Params; onParams: (p: Params) => void; onToggle: () => void; onFocus: () => void;
}) {
  const t = o.template;
  const why = disabledReason(o);
  const st = t.stats;
  const [editing, setEditing] = useState(false);
  const canEdit = editable(t) && why == null;
  const version = presetId(params, o.default_params);
  const tweaked = Object.keys(params).filter((k) => k !== "preset").length;
  return (
    <li className="ws8-card" data-testid={`card-${t.id}`} data-focused={focused} data-selected={selected}
      data-disabled={why != null} aria-current={focused ? "true" : undefined}>
      <div role="group" aria-label={`Template ${index + 1}: ${t.title}`}>
      <div className="ws8-row">
        <strong>{t.title}</strong>
        <span className="ws8-badge">{t.content_class}</span>
        <span className="ws8-badge">{t.engine}</span>
        {selected && <span className="ws8-badge ws8-badge-ok" data-testid={`card-${t.id}-selected`}>selected</span>}
        {version && (
          <span className="ws8-badge" data-testid={`card-${t.id}-version`}>
            {t.presets.find((p) => p.id === version)?.title ?? version}{tweaked ? ` + ${tweaked} tweak${tweaked === 1 ? "" : "s"}` : ""}
          </span>
        )}
        {o.existing_render_id != null && (
          <Link className="ws8-badge" to={`/renders/${o.existing_render_id}`} data-testid={`card-${t.id}-existing`}>
            already rendered: #{o.existing_render_id}
          </Link>
        )}
      </div>
      <p className="ws8-muted">
        {st.renders} renders · {st.approved} approved
        {st.approval_rate != null ? ` · ${(st.approval_rate * 100).toFixed(0)}% approval` : ""}
        {st.avg_plays != null ? ` · ${Math.round(st.avg_plays)} avg plays` : ""}
      </p>
      {why && <p className="ws8-badge ws8-badge-bad" data-testid={`card-${t.id}-disabled`}>Not choosable: {why}</p>}
      {o.still_media_ids.length === 0 && (o.still_status === "queued" || o.still_status === "running") && (
        <p className="ws8-muted" data-testid={`card-${t.id}-still-pending`}>Rendering {t.title} on this clip…</p>
      )}
      {o.still_media_ids.length === 0 && o.still_status === "failed" && (
        <p className="ws8-muted" data-testid={`card-${t.id}-still-failed`}>
          The {t.title} card preview failed; Edit → Preview shows the error.
        </p>
      )}
      {o.still_media_ids.length > 0 && (
        <div className="ws8-stills">
          {o.still_media_ids.slice(0, 3).map((m, i) => (
            <img key={m} src={mediaUrl(m)} loading="lazy" alt={`${t.title} on clip #${clipId}, still ${i + 1} of ${Math.min(3, o.still_media_ids.length)}`} />
          ))}
        </div>
      )}
      {focused && t.preview_media_id != null && (
        <div className="ws8-row">
          <video src={mediaUrl(t.preview_media_id)} muted loop playsInline preload="metadata" autoPlay={!agent}
            controls aria-label={`Generic preview of ${t.title}`} data-testid={`card-${t.id}-preview`} />
          <DownloadVideo mediaId={t.preview_media_id} label={`Download ${t.title} preview`} />
        </div>
      )}
      <div className="ws8-row">
        <button type="button" data-testid={`choose-${t.id}`} aria-pressed={selected} disabled={why != null}
          onClick={onToggle}>Choose {t.title}</button>
        {!focused && (
          <button type="button" data-testid={`focus-${t.id}`} aria-label={`Show preview of ${t.title}`} onClick={onFocus}>Show preview</button>
        )}
        {canEdit && (
          <button type="button" data-testid={`edit-${t.id}`} aria-expanded={editing}
            onClick={() => setEditing((e) => !e)}>{editing ? `Close ${t.title} editor` : `Edit ${t.title}`}</button>
        )}
      </div>
      {editing && canEdit && (
        <TemplateEditor clipId={clipId} template={t} defaults={o.default_params} params={params} onChange={onParams} />
      )}
      </div>
    </li>
  );
}
